<template>
  <b-card class="shadow">
    <div class="mb-4 text-center">
      <h3>{{ $t('auth.forgot-password.title') }}</h3>
      <p v-if="$te('auth.forgot-password.description')">{{ $t('auth.forgot-password.description') }}</p>
    </div>

    <b-alert v-if="err" class="mb-4" show variant="danger">
      {{ (err.extensions ? err.extensions.message : null) || err.message || err }}
    </b-alert>

    <form v-if="sentCode" method="POST" @submit.prevent="submitConfirmationCode">
      <b-form-group>
        <b-input-group>
          <b-form-input autocomplete="email email-address" id="emailAddress" type="email" :placeholder="$t('auth.placeholders.emailAddress')"
            :disabled="Boolean(emailAddress)" required v-model="form.emailAddress"/>
          <b-input-group-append v-if="emailAddress">
            <b-btn class="email-change-btn text-dark" @click="() => $emit('setEmailAddress', null)" tabindex="-1" variant="transparent">
              <font-awesome-icon icon="pencil-alt"/>
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <b-form-group>
        <b-form-input id="code" :placeholder="$t('auth.placeholders.confirmationCode')"
          :disabled="$route.params && $route.params.code" required type="text" v-model="form.confirmationCode"/>
      </b-form-group>
      <b-form-group>
        <b-form-input id="password" type="password" :placeholder="$t('auth.placeholders.password')" :state="formState('password')" v-model="form.password"/>
        <div v-if="typeof formValid.password === 'string'" class="invalid-feedback">
          <template v-if="formValid.password === 'EMPTY'">{{ $t('auth.validation.password-empty') }}</template>
          <template v-else-if="formValid.password === 'TOO_SHORT'">{{ $t('auth.validation.password-too-short') }}</template>
          <template v-else>{{ formValid.password }}</template>
        </div>
      </b-form-group>
      <b-form-group>
        <b-form-input id="passwordConfirmation" type="password" :placeholder="$t('auth.placeholders.passwordConfirm')" :state="formState('passwordConfirm')" v-model="form.passwordConfirm"/>
        <div v-if="typeof formValid.passwordConfirm === 'string'" class="invalid-feedback">
          <template v-if="formValid.passwordConfirm === 'EMPTY'">{{ $t('auth.validation.passwordConfirm-empty') }}</template>
          <template v-else-if="formValid.passwordConfirm === 'CONFIRM_MISMATCH'">{{ $t('auth.validation.passwordConfirm-mismatch') }}</template>
          <template v-else>{{ formValid.passwordConfirm }}</template>
        </div>
      </b-form-group>

      <b-form-group>
        <b-btn class="w-100" :disabled="working" size="md" type="submit" variant="primary">
          <template v-if="working">{{ $t('ctas.verifying') }}</template>
          <template v-else>{{ $t('ctas.verify') }}</template>
        </b-btn>
      </b-form-group>

      <p v-if="canAuthEmailPassword" class="d-flex flex-row justify-content-center align-items-center">
        <a href="#" @click.prevent="resendConfirmationCode">{{ $t('auth.forgot-password.resend') }}</a>
      </p>
    </form>
    <form v-else method="POST" @submit.prevent="resendConfirmationCode">
      <b-form-group>
        <b-input-group>
          <b-form-input autocomplete="email email-address" id="emailAddress" type="email" :placeholder="$t('auth.placeholders.emailAddress')"
            :disabled="Boolean(emailAddress)" required v-model="form.emailAddress"/>
          <b-input-group-append v-if="emailAddress">
            <b-btn class="email-change-btn text-dark" @click="() => $emit('setEmailAddress', null)" tabindex="-1" variant="transparent">
              <font-awesome-icon icon="pencil-alt"/>
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <b-form-group>
        <b-btn class="w-100" :disabled="working" size="md" type="submit" variant="primary">
          <template v-if="working">{{ $t('auth.forgot-password.sending-confirmation-code') }}</template>
          <template v-else>{{ $t('auth.forgot-password.send-confirmation-code') }}</template>
        </b-btn>
      </b-form-group>
    </form>
  </b-card>
</template>

<script>
import authMixin from '@src/mixins/auth';
import base64 from 'base-64';
import formValidationMixin from '@src/mixins/formValidation';
import AuthTriggerChallengeMutation from '@src/graphql/mutations/AuthTriggerChallenge.gql';
import AuthRespondToChallengeMutation from '@src/graphql/mutations/AuthRespondToChallenge.gql';
import { toDataURI } from '@src/lib/utils';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    company: Object,
    emailAddress: {
      type: String,
      default: () => null,
    },
  },
  mixins: [
    authMixin,
    formValidationMixin,
  ],
  data: () => ({
    err: null,
    form: {},
    sentCode: false,
    working: false,
  }),
  created() {
    this.form.emailAddress = this.emailAddress;
  },
  mounted() {
    const { encodedEmail, code } = this.$route.params || {};

    if (encodedEmail && code) {
      const { encodedEmail, code } = this.$route.params;
      this.form.emailAddress = base64.decode(encodedEmail);
      this.form.confirmationCode = code;

      this.$emit('setEmailAddress', this.form.emailAddress);
      this.sentCode = true;
    }

    const firstInput = this.$el.querySelector('input:not([disabled]):first-child');
    firstInput && firstInput.focus();
  },
  metaInfo() {
    return {
      title: this.$t('auth.forgot-password.title'),
    };
  },
  computed: {
    ...mapRoot([ 'isLoggedIn' ]),
    isFormValid() {
      const invalidInputs = Object.keys(this.formValid).filter(key => this.formValid[key] !== true);
      if (invalidInputs.length) return false;
      return true;
    },
  },
  formValidation: {
    password(value) {
      if (this.forcePasswordInvalid) {
        return this.forcePasswordInvalid;
      } else if (value && value.length < 8) {
        return 'TOO_SHORT';
      } else if (!value) {
        return 'EMPTY';
      } else {
        return true;
      }
    },
    passwordConfirm(value, { password }) {
      if (password && value !== password) {
        return 'CONFIRM_MISMATCH';
      } else if (!value) {
        return 'EMPTY';
      } else {
        return true;
      }
    },
  },
  methods: {
    formState(key) {
      return this.formValid[key] === true ? true : (typeof this.formValid[key] === 'string' ? false : null);
    },
    async resendConfirmationCode() {
      try {
        this.working = true;
        this.$emit('setEmailAddress', this.form.emailAddress);

        const { data, errors } = await this.$apollo.mutate({
          mutation: AuthTriggerChallengeMutation,
          variables: {
            challenge: {
              emailAddress: this.form.emailAddress,
              challenge: 'FORGOT_PASSWORD',
              contextData: this.getCognitoContextData(this.form.emailAddress),
            },
          },
          errorPolicy: 'all',
        });

        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('resendConfirmationCode', err));
          throw errors.shift();
        }

        this.$assert(data && data.triggerCognitoChallenge, new Error('Failed to request code'));
        this.sentCode = true;
        this.working = false;
        await this.$nextTick(() => {
          const firstInput = this.$el.querySelector('input:not([disabled]):first-child');
          firstInput && firstInput.focus();
        });
      } catch (err) {
        this.err = err;
        this.working = false;
      }
    },
    async submitConfirmationCode() {
      try {
        this.working = true;

        const { emailAddress, confirmationCode, password, passwordConfirm } = this.form;
        this.$emit('setEmailAddress', emailAddress);

        this.$assert(confirmationCode, this.$t('auth.forgot-password.missing-confirmation-code'), {
          code: 'MISSING_CONFIRMATION_CODE',
        });
        this.$assert(password && password === passwordConfirm, this.$t('auth.errors.passwords-do-not-match'), {
          code: 'PASSWORDS_MISMATCH',
        });

        const { errors } = await this.$apollo.mutate({
          mutation: AuthRespondToChallengeMutation,
          variables: {
            challenge: {
              emailAddress,
              challenge: 'FORGOT_PASSWORD',
              parameters: [
                { key: 'CONFIRMATION_CODE', value: confirmationCode },
                { key: 'NEW_PASSWORD', value: password ? toDataURI('text/plain;charset=utf8', password) : null },
              ],
              contextData: this.getCognitoContextData(emailAddress),
            },
          },
          update: (client, { data: { respondToCognitoChallenge } }) => this.updateAuthResponse(client, respondToCognitoChallenge),
          errorPolicy: 'all',
        });

        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('submitConfirmationCode', err));
          throw errors.shift();
        }

        this.$assert(this.isLoggedIn, 401, new Error('Failed to login after submitting code'));
        await this.afterAuthenticated(this.nextRoute);
      } catch (err) {
        this.err = err;
        this.working = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/Auth/LoginForgot.vue